/**
 * Phase 19 (2026-07-05) — Pending Admin Approval queue (admin only).
 * Used as a tab on /approvals. Lists records in `pending_admin_approval`
 * status across all gated record types and offers Approve / Reject.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, X, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TYPE_LABEL = {
  bank_account: "Bank Account",
  supplier: "Supplier",
  customer: "Customer",
  employee: "Employee",
  invoice: "Invoice",
  manual_invoice: "Manual Invoice",
  loan: "Loan",
  transaction: "Transaction",
  payroll_run: "Payroll Run",
};

function preview(record_type, rec) {
  if (!rec) return "—";
  if (record_type === "bank_account")
    return `${rec.bank_name || ""} · ${rec.account_number || ""} · ${rec.currency || ""}`;
  if (record_type === "supplier" || record_type === "customer" || record_type === "employee")
    return rec.name || "—";
  if (record_type === "invoice" || record_type === "manual_invoice")
    return `${rec.invoice_number || rec.id?.slice(0, 8)} · ${rec.total_amount || rec.total || ""}`;
  if (record_type === "transaction")
    return `${rec.transaction_type || ""} · ${rec.amount || ""} · ${rec.description || ""}`;
  return rec.name || rec.id?.slice(0, 8) || "—";
}

export default function PendingApprovalsTab() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/approvals/pending");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === "system_admin" || user?.full_access === true) load();
  }, [user]);

  if (user?.role !== "system_admin" && user?.full_access !== true) {
    return (
      <Card className="border-slate-200">
        <CardContent className="py-10 text-center text-sm text-slate-500">
          Pending Admin Approval is only visible to system administrators.
        </CardContent>
      </Card>
    );
  }

  const approve = async (record_type, id) => {
    setBusy(true);
    try {
      await api.post(`/admin/approvals/${record_type}/${id}/approve`);
      toast.success(`${TYPE_LABEL[record_type] || record_type} approved`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!rejectFor) return;
    setBusy(true);
    try {
      await api.post(
        `/admin/approvals/${rejectFor.record_type}/${rejectFor.record.id}/reject`,
        { reason: rejectReason || "" },
      );
      toast.success("Rejected");
      setRejectFor(null);
      setRejectReason("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="pending-admin-approval-tab">
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-xl text-[#2F86B5]">Pending Admin Approval</CardTitle>
            <CardDescription>
              Records created by data-entry users awaiting your approval before they post to the GL / dashboard.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" data-testid="pending-count">{items.length} pending</Badge>
            <Button size="sm" variant="outline" onClick={load} disabled={loading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-[#4FA3D1]" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-500" data-testid="pending-empty">
              No records awaiting approval.
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((it) => {
                const r = it.record || {};
                return (
                  <div
                    key={`${it.record_type}-${r.id}`}
                    data-testid={`pending-row-${r.id}`}
                    className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-white p-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                          {TYPE_LABEL[it.record_type] || it.record_type}
                        </Badge>
                        <span className="truncate font-medium text-slate-800">
                          {preview(it.record_type, r)}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        Requested by{" "}
                        <strong>{r.requested_by_email || r.created_by_email || "—"}</strong>{" "}
                        · {r.requested_at || r.created_at || "—"}
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-1.5">
                      <Button
                        size="sm"
                        onClick={() => approve(it.record_type, r.id)}
                        disabled={busy}
                        className="bg-emerald-600 text-white hover:bg-emerald-700"
                        data-testid={`approve-${r.id}`}
                      >
                        <Check className="mr-1.5 h-4 w-4" /> Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setRejectFor(it)}
                        disabled={busy}
                        className="text-rose-700"
                        data-testid={`reject-${r.id}`}
                      >
                        <X className="mr-1.5 h-4 w-4" /> Reject
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject pending record</DialogTitle>
            <DialogDescription>
              The record will be marked <strong>rejected_by_admin</strong> and the
              reason will appear in the audit log.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-sm">Reason</Label>
            <Input
              autoFocus
              placeholder="e.g. duplicate of existing record"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              data-testid="reject-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)}>Cancel</Button>
            <Button variant="destructive" onClick={reject} disabled={busy} data-testid="confirm-reject">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <X className="mr-2 h-4 w-4" />}
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
